import dayjs from 'dayjs'
import request from './request'

/**
 * full_feature_v3 管理端 API 类型与工具函数（Contract Pack 拼写为准）
 */

// ---- 通用 ----
export interface PageData<T> {
  total: number
  page: number
  pageSize: number
  items: T[]
}

/** 错误 message 提取（后端统一 {code,message}） */
export const errMsg = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback

export const fmtTime = (t?: string | null): string =>
  t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '—'

export const fmtDate = (t?: string | null): string =>
  t ? dayjs(t).format('YYYY-MM-DD') : '—'

export const fmtMoney = (v?: number | string | null): string =>
  v == null ? '—' : `¥${Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`

/** CSV 下载（携带鉴权 header，按当前筛选导出） */
export const downloadCsv = async (url: string, filename: string, params?: Record<string, unknown>): Promise<void> => {
  const res = await request.get(url, { params, responseType: 'blob' })
  const blob = new Blob([res.data as BlobPart], { type: 'text/csv;charset=utf-8' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

// ---- 《转介答谢规则》版本（rules_versioning_v1） ----
export interface RuleVersion {
  id: number
  contentZh: string
  contentEn: string | null
  effectiveAt: string
  /** share_reward_rule_end_v1（P-PROJ-948A20898D17 ST2）：规则结束时间（可空，纯展示） */
  effectiveEnd: string | null
  status: string // published | archived
  createdBy: number | null
  createdAt: string
}

// ---- 分享答谢方案（share_reward_rule_end_v1，P-PROJ-948A20898D17 Contract Pack 拼写逐字） ----
// 大标题固定走小程序 i18n「本期答谢方案」，不录入方案类型名/方案名（供图注释口径）。
export interface ShareRewardChip {
  label: string
  labelEn?: string
  value: string
  valueEn?: string
}
export interface ShareRewardSection {
  heading: string
  headingEn?: string
  body: string
  bodyEn?: string
  chips?: ShareRewardChip[]
}
export interface ShareReward {
  title?: string
  titleEn?: string
  effectiveText: string
  effectiveTextEn?: string
  sections: ShareRewardSection[]
  note?: string
  noteEn?: string
}

/**
 * share_reward_by_reward_type_v1（ST3）：落地页 × 答谢类型 一套分享答谢弹窗内容（子表行）。
 * Contract Pack 逐字：GET /admin/project-pages/:id/share-rewards 返回本行数组；
 * content 复用 ShareReward 白名单结构；rewardType 关联含 id/code/name/nameEn。
 */
export interface ProjectPageShareReward {
  id: number
  projectPageId: number
  rewardTypeId: number
  content: ShareReward
  sortOrder: number
  status: 'draft' | 'published' | 'offline'
  rewardType?: RewardTypeNameRef | null
}

// ---- 线索 ----
// 状态枚举（Contract Pack 逐字）：pending/contacted/toured/dealt/verified/rewarded/invalid
export type LeadStatus = 'pending' | 'contacted' | 'toured' | 'dealt' | 'verified' | 'rewarded' | 'invalid'

export const LEAD_STATUS_LABELS: Record<LeadStatus, { label: string; color: string }> = {
  pending: { label: '待跟进', color: 'warning' },
  contacted: { label: '已联系', color: 'processing' },
  toured: { label: '已带看', color: 'cyan' },
  dealt: { label: '已成交', color: 'success' },
  verified: { label: '已核销', color: 'geekblue' },
  rewarded: { label: '已发放', color: 'purple' },
  invalid: { label: '无效', color: 'default' },
}

export const VERIFY_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: '待核验', color: 'warning' },
  verified: { label: '已核验', color: 'success' },
  rejected: { label: '核验拒绝', color: 'error' },
}

export const ASSIGN_TYPE_LABELS: Record<string, string> = {
  duty: '值班派单',
  rotation: '轮派',
  claim: '公海认领',
  manual: '手动转移',
}

export const SOURCE_MODE_LABELS: Record<string, string> = {
  A: '渠道A（扫码）',
  B: '渠道B（录入）',
}

/** 与后端 SHOP_NAME_MAX_LEN 保持契约一致。 */
export const SHOP_NAME_MAX_LEN = 100

export const IDENTITY_LABELS: Record<string, string> = {
  tenant: '租户',
  owner: '业主',
  employee: '企业员工',
  social: '推荐人',
  boss: '老板',
  merchant: '项目商家',
}

export const APPLICATION_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: '待审核', color: 'warning' },
  approved: { label: '已通过', color: 'success' },
  rejected: { label: '已驳回', color: 'error' },
}

// 奖励发放状态机（Contract Pack 逐字）：pending_calc/pending_approve/pending_pay/paid
export const REWARD_REVIEW_LABELS: Record<string, { label: string; color: string }> = {
  pending_calc: { label: '待核算', color: 'default' },
  pending_approve: { label: '待审批', color: 'warning' },
  pending_pay: { label: '待发放', color: 'processing' },
  paid: { label: '已发放', color: 'success' },
}

export const PAY_METHOD_LABELS: Record<string, string> = {
  transfer: '转账',
  cash: '现金',
}

/**
 * lead_intent_category_v1：意向业态中文映射（office/rental/retail → 写字楼/租房/商业）。
 * thankyou_v1（批次 2）：自 Leads/index.tsx 提升为共享导出——成交业态列的 code→中文
 * 兜底映射复用本表（后台新增业态时动态表 name 优先，本表仅覆盖内置三业态）。
 */
export const INTENT_CATEGORY_LABELS: Record<string, string> = { office: '写字楼', rental: '租房', retail: '商业' }

/** thankyou_v1（批次 2）：答谢类型 / 业态 / 方案 状态（复用 ProjectPageStatus，字面量与后端一致） */
export const BENEFIT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: '草稿', color: 'default' },
  published: { label: '已发布', color: 'success' },
  offline: { label: '停用', color: 'warning' },
}

// ---- thankyou_v2（批次 8.2）：答谢体系 V2.0 枚举与标签（Contract Pack 拼写逐字）----

/** 礼遇交付方式（BenefitDeliveryMethod）：contract=随合同 / offline=线下 */
export type BenefitDeliveryMethod = 'contract' | 'offline'

export const BENEFIT_DELIVERY_METHOD_LABELS: Record<BenefitDeliveryMethod, string> = {
  contract: '随合同',
  offline: '线下',
}

/** 礼遇内容来源（BenefitContentSource）：unified=统一礼遇 / tiered=按面积分档 */
export type BenefitContentSource = 'unified' | 'tiered'

export const BENEFIT_CONTENT_SOURCE_LABELS: Record<BenefitContentSource, string> = {
  unified: '统一礼遇',
  tiered: '按面积分档',
}

/** 券结算状态（Reward.settlementStatus）：后端「未核销」判定为 settlementStatus != 'settled' */
export const SETTLEMENT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  unsettled: { label: '未结算', color: 'warning' },
  settled: { label: '已结算', color: 'success' },
}

/**
 * 商户档案状态标签。
 * ST1 的 merchants 表为软删语义，枚举拼写以后端 schema 为权威；此处同时登记
 * active/inactive 与 published/offline 两套既有软删口径，避免集成期渲染成裸英文。
 */
export const MERCHANT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active: { label: '在营', color: 'success' },
  inactive: { label: '已停用', color: 'default' },
  published: { label: '在营', color: 'success' },
  offline: { label: '已停用', color: 'default' },
  draft: { label: '草稿', color: 'warning' },
}

export const UNIT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  available: { label: '可租', color: 'success' },
  leased: { label: '已租', color: 'default' },
  reserved: { label: '已预订', color: 'warning' },
}

export const FOLLOW_UP_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  note: { label: '跟进记录', color: '#2557A7' },
  status_change: { label: '状态流转', color: '#F39C12' },
  tour: { label: '带看', color: '#16A085' },
  system: { label: '系统事件', color: '#7F8C8D' },
}

// ---- 实体类型 ----
export interface ReferrerRef {
  id: number
  name: string
  rid: string
  referrerType?: string
}

export interface Unit {
  id: number
  building: string
  floor: string
  unitNo: string
  area: number | string
  rentPriceMin?: number | string | null
  rentPriceMax?: number | string | null
  status: 'available' | 'leased' | 'reserved'
  createdAt?: string
  updatedAt?: string
}

export interface LeadItem {
  id: number
  name: string
  phone: string
  company?: string | null
  areaIntent?: number | string | null
  industry?: string | null
  notes?: string | null
  /** 预计到访详细时间（visit_poster_v5，ISO 串，可空；旧版路径与后台编辑保留） */
  visitTime?: string | null
  /** 预计到访时间四档文案（lead_form_slim，可空）：三天内/一周内/一个月内/其他 及 en 值；与 visitTime 互斥 */
  visitTimeText?: string | null
  /** 客户性别（lead_gender_v1，可空）：male / female；其他与空展示「—」 */
  gender?: string | null
  sourceMode: 'A' | 'B'
  referrerId?: number | null
  referrer?: ReferrerRef | null
  /** multi_project_display_v1：意向项目（关联 projects.id/name，空为 null） */
  projectId?: number | null
  project?: { id: number; name: string } | null
  /** lead_intent_category_v1：意向业态（office/rental/retail，空为 null） */
  intentCategory?: string | null
  protectUntil?: string | null
  protectionDaysSnapshot?: number | null
  attributionStatus?: 'active' | 'released'
  /** tour_multi_v1：带看次数（后台列表/详情「带看 N 次」展示；老接口不下发时为 undefined） */
  tourCount?: number
  status: LeadStatus
  verificationStatus: 'pending' | 'verified' | 'rejected'
  verifiedBy?: number | null
  verifiedAt?: string | null
  invalidReason?: string | null
  assigneeId?: number | null
  /** assignee_display_username_v1：跟进人用户名（后端补名下发；老接口不下发时为 undefined，前端降级显示 #id） */
  assigneeName?: string | null
  assignmentType?: 'duty' | 'rotation' | 'claim' | 'manual' | null
  createdAt: string
  /** 列表接口的精简成交信息（财务「成交收款」页复用 /admin/leads?status=dealt 时需要） */
  deal?: Pick<DealInfo, 'id' | 'paymentCollectedAt' | 'firstMonthRent' | 'signedAt' | 'dealCategory'> | null
}

export interface FollowUp {
  id: number
  leadId: number
  type: 'note' | 'status_change' | 'tour' | 'system'
  content?: string | null
  nextContactAt?: string | null
  tourAt?: string | null
  tourUnit?: string | null
  tourFeedback?: string | null
  prevStatus?: string | null
  newStatus?: string | null
  operatorId?: number | null
  operatorName?: string | null
  createdAt: string
}

export interface DealInfo {
  id: number
  leadId: number
  unit?: string | null
  unitId?: number | null
  unitRef?: Unit | null
  area?: number | string | null
  unitPrice?: number | string | null
  firstMonthRent?: number | string | null
  leaseMonths?: number | null
  signedAt?: string | null
  paymentCollectedAt?: string | null
  /** thankyou_v1（批次 1 后端）：成交业态 code（business_formats.code，空为 null） */
  dealCategory?: string | null
}

export interface LeadDetail extends LeadItem {
  deal?: DealInfo | null
  followUps?: FollowUp[]
  /** tour_multi_v1：带看台账（lead_tours，tourAt 倒序） */
  tourList?: LeadTourItem[]
  /**
   * reward-anchor-confirm-v1（ST3）：奖励方案锚定（lead_reward_anchors）。
   * 后端未返回（旧版本 / 无首带看锚定）时为 undefined 或 null，前端一律渲染「未锚定」——
   * 故声明为可选，允许 ST1 未部署期间独立构建与灰度。
   */
  anchor?: LeadAnchorView | null
}

/** tour_multi_v1：带看事件台账行（GET /admin/leads/:id 下发） */
export interface LeadTourItem {
  id: number
  leadId: number
  tourAt: string
  tourUnit?: string | null
  tourFeedback?: string | null
  operatorId?: number | null
  operatorName?: string | null
  createdAt: string
}

// ---- reward-anchor-confirm-v1（ST3）：奖励方案锚定（只读展示） ----

/** 锚定状态（Contract Pack 逐字，全层不得改写）：pending_confirm 待确认 / confirmed 已确认 */
export type AnchorStatus = 'pending_confirm' | 'confirmed'

export const ANCHOR_STATUS_LABELS: Record<AnchorStatus, { label: string; color: string }> = {
  pending_confirm: { label: '待确认', color: 'orange' },
  confirmed: { label: '已确认', color: 'green' },
}

/**
 * 锚定方案快照（lead_reward_anchors.plan_snapshot，镜像 lead_benefit_snapshots 列集，
 * Contract Pack 键集逐字）。后端 Json 下发，前端按只读消费；缺键一律回落 '—'。
 */
export interface AnchorPlanSnapshot {
  benefitPlanId?: number | null
  benefitPlanName?: string | null
  rewardTypeCode?: string | null
  cashRewardText?: string | null
  cashRewardTextEn?: string | null
  inKindText?: string | null
  inKindTextEn?: string | null
  referredBenefitText?: string | null
  referredBenefitTextEn?: string | null
  benefitDeliveryMethod?: BenefitDeliveryMethod | null
  benefitContentSource?: BenefitContentSource | null
  /** 分档规则数组（形态同 backend/src/services/snapshot.ts::TierRuleJson）；非数组时不渲染分档表 */
  tierRulesJson?: AnchorTierRule[] | null
  effectiveFrom?: string | null
  effectiveTo?: string | null
}

/** 锚定快照内的单条分档规则：区间左闭右开（areaTo=null 表示无上限） */
export interface AnchorTierRule {
  areaFrom?: number | string | null
  areaTo?: number | string | null
  benefitContent?: string | null
  couponValue?: number | string | null
  sortOrder?: number | null
}

/**
 * 锚定记录下发形状（AnchorView，Contract Pack 键集逐字）。
 * rewardTypeId/rewardTypeCode 仅 admin 详情下发（mp 端不含）。
 */
export interface LeadAnchorView {
  id: number
  leadId: number
  /** 客户姓氏脱敏（maskName：首字 + 固定 **，空名 → **） */
  customerNameMasked: string
  /** 首次带看时间（= 带看入参 tourAt） */
  visitAt: string
  protectUntil: string | null
  protectionDays: number | null
  /** 意向业态快照（lead.intentCategory：office/rental/retail） */
  businessFormat: string | null
  status: AnchorStatus
  confirmedAt: string | null
  planSnapshot: AnchorPlanSnapshot
  /** admin 附加：答谢类型 */
  rewardTypeId?: number | null
  rewardTypeCode?: string | null
}

export interface RewardItem {
  id: number
  leadId: number
  referrerId: number
  referrer?: ReferrerRef
  lead?: {
    id: number
    name: string
    phone: string
    /** thankyou_v1（批次 1 后端）：GET /admin/rewards include lead.deal.dealCategory */
    deal?: { dealCategory?: string | null } | null
  }
  amount: number | string
  originalAmount?: number | string | null
  status: 'pending' | 'paid'
  reviewStatus: 'pending_calc' | 'pending_approve' | 'pending_pay' | 'paid'
  payMethod?: 'transfer' | 'cash' | null
  transactionNo?: string | null
  signedAt?: string | null
  paidAt?: string | null
  receiptUrl?: string | null
  receiptUploadedAt?: string | null
  createdAt: string
  /** thankyou_v1（批次 1 后端）：转介人答谢文字快照（rewards.referrer_reward_text） */
  referrerRewardText?: string | null
  /** thankyou_v1（批次 1 后端）：被推荐人礼遇文字快照（rewards.referred_benefit_text） */
  referredBenefitText?: string | null
  /** 成交业态 code：真实行取自 lead.deal.dealCategory，待核算虚拟行为顶层 dealCategory */
  dealCategory?: string | null
  /** 成交业态中文名（GET /admin/rewards 由后端动态映射附加；未命中为 null） */
  dealCategoryLabel?: string | null
  /**
   * thankyou_v2（批次 8.2）财务三栏（Contract Pack 逐字，均可空 = 历史行未回填）：
   * grossAmount=应发(税前)、taxAmount=代扣个税(20%)、netAmount=实发。
   * 入驻商家渠道三栏恒 0（金额口径 = couponAmount 券采购额），前端置灰显示。
   */
  grossAmount?: number | string | null
  taxAmount?: number | string | null
  netAmount?: number | string | null
  /** 答谢实物内容（商会年卡等，不并入应税所得） */
  inKindContent?: string | null
  /** 套档结果（后端 applyTierRules 按成交面积写入，例「401㎡ → 2万档」） */
  couponTierResult?: string | null
  /** 券采购额（= 套档券面值） */
  couponAmount?: number | string | null
  /** 券采购单号 / 商家发票号（7.5 请款审批挂钩点） */
  couponPurchaseNo?: string | null
  merchantInvoiceNo?: string | null
  /** 券发放登记：发放人（后端取当前账号）/ 发放时间 / 券号或凭证 URL / 结算状态 */
  couponIssuer?: string | null
  couponIssuedAt?: string | null
  couponNoOrProof?: string | null
  settlementStatus?: string | null
  /** 后端派生的渠道口径：cash=现金三栏 / coupon=券额度（rewardTypeCode==='merchant'） */
  displayMode?: 'cash' | 'coupon' | null
  /**
   * 待核算虚拟行标记（后端 /admin/rewards?includePending=1 合成，非 rewards 表真实记录）。
   * 虚拟行 id 为负数（= -leadId），前端据此禁用调整/审批/发放等针对真实奖励单的操作。
   */
  _pending?: boolean
  /** 虚拟行专用：该成交的两押一租是否已到账（决定提示文案） */
  _depositConfirmed?: boolean
}

export interface ReferrerItem {
  id: number
  rid: string
  name: string
  phone: string
  identity: string
  /** identity_other_v1：选「其他/social」时用户自定义身份（选填≤30字），非 social 为 null */
  identityOther?: string | null
  /** 注册自述店铺名称，独立于商户关联及答谢配置。 */
  shopName?: string | null
  unit?: string | null
  referrerType: 'enterprise' | 'internal'
  status: 'pending' | 'approved' | 'rejected'
  tags?: string | null
  createdAt: string
  applications?: Array<{ id: number; status: string; createdAt: string }>
  _count?: { leads: number }
  /** thankyou_v1（批次 1 后端）：答谢类型 / 绑定方案（列表与详情 include 嵌套对象） */
  rewardTypeId?: number | null
  rewardPlanId?: number | null
  /**
   * referrer_multi_plan（2026-09-28）：多业态绑定方案 id 列表（每个 businessFormatId 至多一个，null=通用）。
   * 旧列 rewardPlanId 保留，后端写入时同步为 rewardPlanIds[0] ?? null；读取以 rewardPlanIds 为准，
   * 为空时回落 rewardPlanId。
   */
  rewardPlanIds?: number[] | null
  rewardType?: RewardTypeNameRef | null
  rewardPlan?: { id: number; name: string; nameEn?: string | null } | null
  /**
   * thankyou_v2（批次 8.2）转介人档案扩 7 字段（Contract Pack 逐字）：
   * merchantId/couponUsageNote/deliveryResponsible/agreementNo/qualificationFiles/
   * qualificationReviewer+At/qualificationValidUntil。
   * 入驻商家（type.code==='merchant'）：agreementNo+merchantId+couponUsageNote 必填（后端 400）；
   * needQualification 类型：qualificationFiles 非空否则后端 400。
   */
  merchantId?: number | null
  merchant?: MerchantRef | null
  couponUsageNote?: string | null
  /** 礼遇交付责任方（入驻商家固定「招商运营发放」，前端只读） */
  deliveryResponsible?: string | null
  agreementNo?: string | null
  /** 资质材料附件 URL 列表（图片 / PDF，可多张，走 POST /admin/uploads/document）。
   *  运行时形态：后端 DB 列是逗号分隔 TEXT，GET 返回字符串；前端表单态是数组。
   *  读取统一经 toFiles() 归一化为数组。 */
  qualificationFiles?: string | string[] | null
  qualificationReviewer?: string | null
  qualificationReviewedAt?: string | null
  qualificationValidUntil?: string | null
  /** referrer_share_stats（2026-11-03）：该推荐人生成的留位卡数（liuwei_card_events 聚合） */
  cardCount?: number
  /** referrer_share_stats（2026-11-03）：该推荐人分享链接被打开次数（share_open_logs 聚合） */
  openCount?: number
}

/** 答谢类型嵌套引用（后端 select id/code/name/nameEn） */
export interface RewardTypeNameRef {
  id: number
  code: string
  name: string
  nameEn?: string | null
  /** thankyou_v2：该类型是否需资质校验（入驻商家 / 商会 = true） */
  needQualification?: boolean
}

/** 商户档案嵌套引用 */
export interface MerchantRef {
  id: number
  name: string
  nameEn?: string | null
}

// ---- 答谢体系配置（thankyou_v1 批次 2，字段逐字对 schema.prisma）----

/** 答谢类型（reward_types 表行） */
export interface RewardType {
  id: number
  code: string
  name: string
  nameEn?: string | null
  sortOrder: number
  status: 'draft' | 'published' | 'offline'
  /** 兜底类型唯一（仅「标准」true） */
  isDefault?: boolean
  /** 内置类型 false：不可停用（前端禁用按钮 + 后端 409 双保险） */
  deletable: boolean
  /** thankyou_v2（批次 8.2）：需资质校验类型（入驻商家 / 商会），绑定转介人须传资质附件 */
  needQualification?: boolean
  createdAt?: string
  updatedAt?: string
  _count?: { benefitPlans?: number; referrers?: number }
}

/** 业态（business_formats 表行） */
export interface BusinessFormat {
  id: number
  code: string
  name: string
  nameEn?: string | null
  sortOrder: number
  status: 'draft' | 'published' | 'offline'
  /** 内置三业态 false：不可停用 */
  deletable: boolean
  createdAt?: string
  updatedAt?: string
  _count?: { benefitPlans?: number }
}

/** 答谢方案（benefit_plans 表行，GET 列表 include rewardType/businessFormat 嵌套） */
export interface BenefitPlan {
  id: number
  rewardTypeId: number
  businessFormatId?: number | null
  name: string
  nameEn?: string | null
  referrerRewardText?: string | null
  referrerRewardTextEn?: string | null
  referredBenefitText?: string | null
  referredBenefitTextEn?: string | null
  effectiveFrom?: string | null
  effectiveTo?: string | null
  coverUrl?: string | null
  sortOrder: number
  status: 'draft' | 'published' | 'offline'
  updatedBy?: number | null
  createdAt?: string
  updatedAt?: string
  rewardType?: { id: number; code: string; name: string; nameEn?: string | null } | null
  businessFormat?: { id: number; code: string; name: string; nameEn?: string | null } | null
  /**
   * thankyou_v2（批次 8.2）方案扩 5 字段（Contract Pack 逐字）。
   * benefitTiered 由 benefitContentSource 派生（tiered → true），前端只读展示、不单独提交语义。
   */
  benefitDeliveryMethod?: BenefitDeliveryMethod | null
  benefitContentSource?: BenefitContentSource | null
  benefitTiered?: boolean
  rewardCashText?: string | null
  rewardCashTextEn?: string | null
  rewardInKindText?: string | null
  rewardInKindTextEn?: string | null
  /** 礼遇分档子表（GET /admin/benefit-plans include tiers；POST/PATCH body 带 tiers[]） */
  tiers?: BenefitPlanTier[]
}

/** 礼遇分档子表行（benefit_plan_tiers）：areaFrom 含、areaTo 不含（null=无上限） */
export interface BenefitPlanTier {
  id?: number
  benefitPlanId?: number
  areaFrom: number | string
  areaTo?: number | string | null
  benefitContent: string
  couponValue?: number | string | null
  sortOrder?: number
}

/**
 * 商户档案（merchants 表行，thankyou_v2 批次 8.2 新建）。
 * 仅软删（无物理 DELETE 语义）；写操作挂 MERCHANT_MANAGE。
 */
export interface Merchant {
  id: number
  name: string
  nameEn?: string | null
  contactName?: string | null
  contactPhone?: string | null
  status: string
  note?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface ReferrerDetail extends ReferrerItem {
  stats: {
    totalLeads: number
    validLeads: number
    dealtLeads: number
    paidRewardTotal: number | string
  }
}

export interface ApplicationItem {
  id: number
  referrerId: number
  realName: string
  phone: string
  identity: string
  unit?: string | null
  reason?: string | null
  /** R11 identity_proof_v11：tenant/owner/employee 申请时上传的身份证明材料 URL */
  proofImageUrl?: string | null
  sourceChannel?: string | null
  status: 'pending' | 'approved' | 'rejected'
  reviewedBy?: number | null
  reviewedAt?: string | null
  reviewNote?: string | null
  createdAt: string
  referrer?: { id: number; name: string; rid: string }
}

// ---- 转介人资料修改审核（R1）----
/** 修改字段中文名（changes JSON 的 key → 显示名） */
export const REFERRER_CHANGE_FIELD_LABELS: Record<string, string> = {
  name: '姓名',
  phone: '手机号',
  identity: '身份',
  unit: '所在单元',
}

/** changes JSON 结构：{ field: { old, new } } */
export interface ReferrerChangeItem {
  id: number
  referrerId: number
  changes: Record<string, { old: unknown; new: unknown }>
  /** R11 identity_proof_v11：变更身份为 tenant/owner/employee 时上传的证明材料 URL */
  proofImageUrl?: string | null
  status: 'pending' | 'approved' | 'rejected'
  reviewedBy?: number | null
  reviewedAt?: string | null
  reviewNote?: string | null
  createdAt: string
  referrer?: { id: number; name: string; rid: string }
}

export interface DutyShift {
  id: number
  adminUserId: number
  startAt: string
  endAt: string
  /** rental_consultant_role_v1：operator=招商顾问 / rental_consultant=租房顾问 / null=存量 operator */
  role?: string | null
  createdBy?: number | null
  createdAt?: string
}

/** dutyshift_batch_delete_replace_v1：POST /admin/duty-shifts/batch-delete 响应 data（dryRun 预览与实删同构） */
export interface DutyShiftBatchDeleteResult {
  count: number
  items: Array<{ id: number; adminUserId: number; startAt: string; endAt: string }>
  applied: boolean
}

export interface ConfigItem {
  id: number
  key: string
  value: string
  updatedBy?: number | null
  updatedAt?: string
}

export interface AuditLogItem {
  id: number
  adminUserId?: number | null
  action: string
  targetType?: string | null
  targetId?: string | null
  detail?: string | null
  ip?: string | null
  createdAt: string
}

export interface DashboardStats {
  cards: { totalLeads: number; monthlyLeads: number; dealtLeads: number; activeReferrers: number }
  todos: {
    pendingAssignment: number
    pendingVerification: number
    pendingRewards: number
    pendingApplications: number
  }
  expiringProtections: number
}

export interface DashboardCharts {
  trend: Array<{ date: string; count: number }>
  sourcePie: Array<{ sourceMode: string; count: number }>
  funnel: Array<{ status: LeadStatus; count: number }>
  starRanking: Array<{ referrerId: number; name: string; leadCount: number }>
}

// dashboard_analytics_v1：GET /admin/dashboard/analytics 响应 data（契约见 planning/dashboard-analytics-20261027-plan.md）
export interface AnalyticsPoint {
  date: string
  cards: number
  opens: number
  visitors: number
}

export interface AnalyticsMonthPoint {
  month: string
  cards: number
  opens: number
  visitors: number
}

export interface AnalyticsData {
  totals: { cards: number; opens: number; visitors: number }
  daily: AnalyticsPoint[]
  monthly: AnalyticsMonthPoint[]
}
